import { ClinicOrder, ClinicProduct, Payment, SupplierPublicBrand } from '../types';
import { Business, BusinessInvitation, BusinessMembership } from '../types/business';
import {
  MOCK_BUSINESSES,
  MOCK_BUYER_ACCOUNTS,
  MOCK_INVITATIONS,
  MOCK_MEMBERSHIPS,
} from './fixtures/users';
import { MOCK_PRODUCTS } from './fixtures/products';
import { MOCK_VIRTUAL_PRODUCTS } from './fixtures/virtual-products';
import { MOCK_SUPPLIER_BRANDS, supplierBriefFor } from './fixtures/suppliers';

type CartLine = { productId: string; quantity: number };
type CartRecord = { userId: string; items: CartLine[] };

type DbShape = {
  businesses: Business[];
  memberships: BusinessMembership[];
  invitations: BusinessInvitation[];
  products: ClinicProduct[];
  suppliers: SupplierPublicBrand[];
  carts: CartRecord[];
  orders: ClinicOrder[];
  payments: Payment[];
};

const withSupplier = (p: ClinicProduct): ClinicProduct => ({
  ...p,
  supplier: p.supplier ?? supplierBriefFor(p.brand),
});

const allProducts = [...MOCK_PRODUCTS, ...MOCK_VIRTUAL_PRODUCTS].map(withSupplier);

const initial: DbShape = {
  businesses: MOCK_BUSINESSES.map((b) => ({ ...b })),
  memberships: MOCK_MEMBERSHIPS.map((m) => ({ ...m })),
  invitations: MOCK_INVITATIONS.map((i) => ({ ...i })),
  products: allProducts,
  suppliers: MOCK_SUPPLIER_BRANDS.map((s) => ({ ...s })),
  carts: [],
  orders: [],
  payments: [],
};

export const db: DbShape = initial;

export const findBusinessById = (businessId: string): Business | undefined =>
  db.businesses.find((b) => b.id === businessId);

export const findActiveMembershipByUserId = (
  userId: string,
): BusinessMembership | undefined =>
  db.memberships.find((m) => m.userId === userId && m.status === 'ACTIVE');

export const findActiveBusinessByUserId = (
  userId: string,
): { business: Business; membership: BusinessMembership } | undefined => {
  const membership = findActiveMembershipByUserId(userId);
  if (!membership) return undefined;
  const business = findBusinessById(membership.businessId);
  if (!business) return undefined;
  return { business, membership };
};

export const findProductById = (productId: string): ClinicProduct | undefined =>
  db.products.find((p) => p.id === productId);

export const findSupplierBySlug = (slug: string): SupplierPublicBrand | undefined =>
  db.suppliers.find((s) => s.publicSlug === slug);

export const findSupplierById = (id: string): SupplierPublicBrand | undefined =>
  db.suppliers.find((s) => s.supplierId === id);

export const getOrCreateCart = (userId: string): CartRecord => {
  let cart = db.carts.find((c) => c.userId === userId);
  if (!cart) {
    cart = { userId, items: [] };
    db.carts.push(cart);
  }
  return cart;
};

export const findBuyerAccountByUserId = (userId: string) =>
  MOCK_BUYER_ACCOUNTS.find((a) => a.userId === userId);
